/**
 * Info-icoon met klikbare popover (werkt op desktop én mobiel/touch).
 * De native title blijft als snelle hover-tooltip; klik/tik opent direct
 * een leesbaar paneel, nog een klik of een klik buiten het paneel sluit het.
 *
 * De popover is position:fixed en wordt bij openen op de viewport uitgelijnd:
 * absolute positionering werd afgekapt door table-containers (overflow) en
 * hing bij links geplaatste iconen buiten beeld.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';

const InfoDot = ({ text, wide = false }) => {
  const [pos, setPos] = useState(null);   // null = dicht; {top,left,width} = open
  const ref = useRef(null);

  const openPopover = () => {
    const r = ref.current.getBoundingClientRect();
    const width = Math.min(wide ? 400 : 280, Math.round(window.innerWidth * 0.9));
    // Standaard rechts uitgelijnd op het icoon, maar altijd binnen de viewport
    const left = Math.max(8, Math.min(r.right - width, window.innerWidth - width - 8));
    setPos({ top: r.bottom + 6, left, width });
  };

  useEffect(() => {
    if (!pos) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setPos(null);
    };
    const onScroll = () => setPos(null);   // anker verschuift -> dicht i.p.v. zweven
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [pos]);

  const open = pos !== null;

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => (open ? setPos(null) : openPopover())}
        aria-label="Uitleg"
        aria-expanded={open}
        title={open ? undefined : text}
        style={{
          background: 'transparent', border: 'none', padding: '2px',
          cursor: 'pointer', display: 'inline-flex', lineHeight: 0,
          color: open ? 'var(--color-blue-500)' : 'var(--color-neutral-400)',
        }}
      >
        <Info size={15} strokeWidth={2.2} />
      </button>
      {open && (
        <span
          role="tooltip"
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
            zIndex: 'var(--z-tooltip, 500)',
            maxHeight: `calc(100vh - ${pos.top}px - 12px)`, overflowY: 'auto',
            background: 'var(--surface-raised, var(--surface))',
            color: 'var(--color-neutral-700)',
            border: '1px solid var(--color-neutral-200)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            padding: '10px 12px',
            fontSize: '0.78rem', lineHeight: 1.55, fontWeight: 400,
            textAlign: 'left', textTransform: 'none', letterSpacing: 0,
            whiteSpace: 'normal',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default InfoDot;
